import { computed, reactive, ref, watch } from 'vue'

/**
 * ✅ Successfully migrated from VueUse to Nuxt color-mode module
 * 
 * This file provides backward compatibility for all existing usage patterns:
 * - `const colorMode = useColorMode()` - Works with both .value and .preference
 * - `const { isDark } = useTheme()` - VueUse-like API
 * - `const cm = computed(() => colorMode.value || 'light')` - Template usage
 * 
 * All existing components should work without changes.
 */

/**
 * A wrapper around Nuxt's color-mode module that provides a stable interface
 * and maintains backward compatibility with VueUse patterns.
 * 
 * This composable should be used instead of calling useColorMode()
 * multiple times in the same component or in template expressions.
 */
export const useTheme = () => {
  // Use Nuxt's color-mode module directly (before we override useColorMode)
  const nuxtColorMode = useNuxtColorMode()
  
  // Create stable computed properties to prevent reactivity issues
  const isDark = computed(() => nuxtColorMode.value === 'dark')
  const isLight = computed(() => nuxtColorMode.value === 'light')
  const isSystem = computed(() => nuxtColorMode.preference === 'system')
  
  // Helper for components that check both value and current theme
  const isDarkMode = computed(() => nuxtColorMode.value === 'dark')
  const isLightMode = computed(() => nuxtColorMode.value === 'light')
  
  // Get system preference (simulate usePreferredDark behavior)
  const prefersDark = computed(() => {
    if (import.meta.client) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return false
  })
  
  // Toggle function
  const toggleColorMode = () => {
    if (nuxtColorMode.value === 'dark') {
      nuxtColorMode.preference = 'light'
    } else {
      nuxtColorMode.preference = 'dark'
    }
  }
  
  // Set specific mode
  const setColorMode = (mode: 'light' | 'dark' | 'auto' | 'system') => {
    if (mode === 'auto' || mode === 'system') {
      nuxtColorMode.preference = 'system'
    } else {
      nuxtColorMode.preference = mode
    }
  }
  
  return {
    // Reactive values - maintain VueUse-like API
    isDark,
    isLight,
    isSystem,
    isDarkMode,
    isLightMode,
    prefersDark,
    
    // Functions
    toggleColorMode,
    setColorMode,
    
    // Direct access to the color mode value (VueUse compatibility)
    value: computed({
      get: () => nuxtColorMode.value,
      set: (newValue: string) => {
        nuxtColorMode.preference = newValue
      }
    }),
    
    // Backward compatibility for existing code
    current: {
      get value() {
        return nuxtColorMode.value
      },
      set value(newValue) {
        nuxtColorMode.preference = newValue
      }
    },
    
    // Preference information
    preference: {
      get value() {
        return nuxtColorMode.preference
      },
      set value(newValue) {
        nuxtColorMode.preference = newValue
      },
      get system() {
        return prefersDark.value ? 'dark' : 'light'
      }
    },
    
    // Expose the underlying Nuxt color mode for advanced usage
    $colorMode: nuxtColorMode
  }
}

// Alias for Nuxt's useColorMode to avoid conflicts
export const useNuxtColorMode = () => {
  // This will use the actual Nuxt color-mode composable
  const { $colorMode } = useNuxtApp()
  return $colorMode
}

// Create a global instance that can be used directly as useColorMode replacement
let globalColorModeInstance: any = null

/**
 * Drop-in replacement for useColorMode() that works with existing code patterns
 * This maintains compatibility with both VueUse and Nuxt color-mode usage patterns
 */
export const useColorMode = () => {
  // Return cached instance to prevent multiple reactive instances
  if (globalColorModeInstance) {
    return globalColorModeInstance
  }
  
  // Get Nuxt's color mode
  const nuxtColorMode = useNuxtColorMode()
  
  // Create a ref-like object that works with .value access
  const colorModeRef = ref(nuxtColorMode.value)
  
  // Watch for changes in nuxt color mode and update our ref
  watch(() => nuxtColorMode.value, (newValue) => {
    colorModeRef.value = newValue
  })
  
  // Create the enhanced object that combines ref behavior with Nuxt properties
  const enhancedColorMode = {
    // Ref-like behavior for VueUse compatibility
    get value() {
      return nuxtColorMode.value
    },
    set value(newValue: string) {
      nuxtColorMode.preference = newValue
    },
    
    // Nuxt color-mode properties
    get preference() {
      return nuxtColorMode.preference
    },
    set preference(newValue: string) {
      nuxtColorMode.preference = newValue
    },
    
    get unknown() {
      return nuxtColorMode.unknown
    },
    
    get forced() {
      return nuxtColorMode.forced
    }
  }
  
  // Cache the instance
  globalColorModeInstance = enhancedColorMode
  
  return enhancedColorMode
} 